// ─────────────────────────────────────────────────────────────────────────────
// formatMatching — the FORMAT-step matching RULES (#2066).
//
// `WizardStepFormat.jsx` had these rules inline: which catalog rows a TYPE tile
// admits, whether a selection scores on the WA ten-ring family, which rows a
// catalog search keeps, and — the worst of them — a distance→face POLICY
// (`distance <= 25 ? "wa40" : "wa122"`) typed straight into a component. A rule
// living in a component cannot be unit-tested, cannot be shared, and drifts the
// moment a second surface needs the same answer (exactly what #2027 fixed for
// `matchesFormatFilter`).
//
// These are PURE predicates over catalog rows — no React, no wizard state. The
// component keeps presentation; this module answers "does this row match?" and
// "what face does this distance imply?".
//
// NOT here: the discipline row→filter table (`FORMAT_TYPE_FILTERS`) and its
// per-row matcher (`matchesFormatFilter`) used to live in `lib/roundConstants.js`
// and be re-exported through this module. Both are RETIRED (#2097) — #1426 left
// no user path that selects a discipline, so every reachable `formatType`
// ('practice' / 'catalog' / 'custom' / null) resolved to the null "match all"
// filter. `filterRoundTypesForFormat(roundTypes, formatType)` therefore ignored
// its second argument in every reachable call, and is replaced below by
// `nonLegacyRoundTypes`, which says what it actually did.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The catalog rows the FORMAT step may offer (#2097).
 *
 * Legacy rounds are never offered; everything else is. This is the whole of what
 * the retired `formatType` filter chain evaluated to once the discipline tiles
 * became unreachable — the search box (`matchesCatalogSearch`) is what narrows
 * the list for the archer now.
 *
 * @param {Array} roundTypes  the whole catalog (GET /api/round-types)
 * @returns {Array} the offerable subset, in catalog order
 */
export function nonLegacyRoundTypes(roundTypes) {
  return (roundTypes ?? []).filter((rt) => rt.environment !== "legacy");
}

/**
 * Is this a COURSE-shaped selection (Field / Hunter / Animal / 3D)?
 *
 * Course rounds walk a set of targets rather than shooting a distance
 * schedule, so they carry no `distances`. Detected from the DATA — every
 * matching row has empty/null distances — rather than from a hardcoded list of
 * tile keys, so a newly seeded course round needs no change here. An empty
 * candidate list is NOT a course type (nothing has been established yet).
 *
 * @param {Array} rounds  the formatType-filtered rows
 * @returns {boolean}
 */
export function isCourseCatalog(rounds) {
  if (!rounds || rounds.length === 0) return false;
  return rounds.every((rt) => !rt.distances || rt.distances.length === 0);
}

/**
 * Does the current selection score on the WA ten-ring family, where the
 * compound X-ring rule applies (#1401)?
 *
 * The SELECTED catalog round is the ONLY answer: its own `scoring_family`.
 * There used to be a `formatType` fallback for "nothing selected yet", via the
 * `WA_TEN_RING_TYPES` tile-key set — but #2097 retired the discipline tiles, so
 * the only reachable `formatType` values became 'practice' / 'catalog' /
 * 'custom' / null, none of which was ever in that set. The fallback was
 * therefore permanently false and is gone (#2162): with nothing selected this
 * returns false, exactly as it did before.
 *
 * @param {object|null} selectedRt  the selected catalog row, if any
 * @returns {boolean}
 */
export function isWaTenRingFormat(selectedRt) {
  return selectedRt?.scoring_family === "WA_TEN_RING";
}

// The GNAS/imperial tile is the one place metric and imperial families co-exist
// under a single tile (#1415), so it is the only place the unit filter is
// offered. A row is imperial by its FACE, not its unit column: the imperial
// 5-zone and Worcester faces are the imperial-family faces.
const IMPERIAL_FACES = new Set(["imperial5zone", "worcester"]);

/**
 * Is this row shot on an imperial-family face?
 *
 * @param {object} rt  a catalog row
 * @returns {boolean}
 */
export function isImperialFace(rt) {
  return IMPERIAL_FACES.has(rt?.face);
}

/**
 * Does a catalog row survive the FORMAT step's search box + metric/imperial
 * filter (#1428)?
 *
 * Name+org substring match (case-insensitive), plus the GNAS-only
 * metric/imperial split. An empty query and an "all" unit filter both match
 * everything, so the caller can apply this unconditionally.
 *
 * @param {object} rt  a catalog row
 * @param {object} [opts]
 * @param {string} [opts.query]  raw search text (trimmed/lowercased here)
 * @param {string} [opts.unitFilter]  'all' | 'metric' | 'imperial'
 * @returns {boolean}
 */
export function matchesCatalogSearch(rt, { query = "", unitFilter = "all" } = {}) {
  const q = query.trim().toLowerCase();
  if (q && !`${rt.name} ${rt.org}`.toLowerCase().includes(q)) return false;
  if (unitFilter === "imperial" && !isImperialFace(rt)) return false;
  if (unitFilter === "metric" && isImperialFace(rt)) return false;
  return true;
}

// The distance at or below which a practice session defaults to the indoor
// face. 25 m is the longest distance shot indoors on a 40 cm face (WA 25 m /
// Portsmouth-class rounds); past it the archer is outdoors on the big face.
const INDOOR_FACE_MAX_DISTANCE_M = 25;

/**
 * The default target face for a practice DISTANCE (#1416).
 *
 * This is a POLICY, not a lookup: a short session is shot on the indoor 40 cm
 * face, anything past 25 m on the big outdoor 122 cm face. It seeds both
 * `selectedFace` and `tweaks.face` on the FORMAT step so the ARCHERS step
 * inherits a sensible default even when the archer taps nothing — and it is
 * re-applied on every distance change to propagate the new default to archers
 * still sitting on the OLD distance's default.
 *
 * Non-numeric input coerces via Number(), so a form's string value ("18") is
 * accepted; NaN falls to the outdoor face rather than throwing.
 *
 * @param {number|string} distance  distance in metres
 * @returns {string} a face code
 */
export function faceForDistance(distance) {
  return Number(distance) <= INDOOR_FACE_MAX_DISTANCE_M ? "wa40" : "wa122";
}

/**
 * The pair of face codes a practice DISTANCE offers as chips (#2312).
 *
 * `faceForDistance` above picks a single "smart default" and cannot be
 * changed without breaking every existing caller (#1416's propagation logic
 * keys off it). But that single answer is wrong for a junior: JOAD shoots as
 * close as 9 m, and a beginner at that range wants the option of the bigger,
 * more forgiving 60 cm face — not just the standard 40 cm indoor face every
 * longer indoor distance also defaults to. Rather than change the existing
 * policy, this exposes BOTH faces for the bucket `faceForDistance` would have
 * picked from, ascending by size, so the FORMAT step can render them as chips
 * and let the archer (or a "9 m defaults to the bigger face" caller) choose.
 *
 * Same ≤25 m indoor / >25 m outdoor split and the same Number() coercion as
 * `faceForDistance`, so the two policies never disagree about which BUCKET a
 * distance falls in — only about how many options that bucket offers.
 *
 * @param {number|string} distance  distance in metres
 * @returns {string[]} two face codes, ascending by physical size
 */
export function faceOptionsForDistance(distance) {
  return Number(distance) <= INDOOR_FACE_MAX_DISTANCE_M
    ? ["wa40", "wa60"]
    : ["wa80", "wa122"];
}

// One source of truth for the yard→metre conversion on the FRONTEND — mirrors
// the backend's `app.segments.YARD_TO_M` (#2342). `Round.distance_m` and every
// metres-consuming policy (the face-by-distance threshold above, handicap,
// stats `by_distance`) must always see METRES, even when the archer picked a
// yard chip — the native yard value is for DISPLAY only (the round's inline
// `{distance, unit}` segment).
export const YARD_TO_M = 0.9144;

/**
 * A native distance value, converted to whole metres (#2342).
 *
 * `faceForDistance` / `faceOptionsForDistance` above are metres-only policies
 * — a yard distance must be converted BEFORE calling them, so a 20 yd Simple
 * Round distance (≈18.3 m) still offers the indoor 40/60 cm chips, not the
 * outdoor pair a raw "20" would wrongly imply. `unit: 'm'` (or omitted) is a
 * no-op rounding.
 *
 * @param {number|string} distance  native distance value
 * @param {string} [unit]  "m" | "yd"
 * @returns {number} whole metres
 */
export function toMetres(distance, unit) {
  const n = Number(distance) || 0;
  return unit === "yd" ? Math.round(n * YARD_TO_M) : Math.round(n);
}

// ── Simple Round distance chips + tree (#2348) ────────────────────────────────
// Owner feedback on the #2342 sandbox test: 13 metre chips wrapped to three
// rows at 390 px and buried the common picks ("distance may work better as a
// dropdown with a tree selector … plus chips for a few common distances and
// ones you've used before"). The chip row now shows only the common distances
// + the archer's recent ones; every other indoor/outdoor distance, in either
// unit, lives behind the `DistanceTree` popover — unit (m/yd) › Indoor/Outdoor
// › distance, reusing the FormatTree catalog picker's tree look. These lists
// are the single source both `PracticeForm` and `DistanceTree` read, so the
// row and the tree can never disagree about what a "common" distance is.

// The 3 chips shown directly on the row for each unit — the distances most
// archers actually shoot most often.
export const PRACTICE_COMMON_M = [18, 30, 70];
export const PRACTICE_COMMON_YD = [20, 40, 60];

// Simple Round "Where" toggle (owner spec 2026-09-16): with an explicit
// Indoor | Outdoor choice on the sheet, the 3 common chips are filtered to
// that branch, per unit. The flat `PRACTICE_COMMON_M/YD` lists above stay for
// the legacy wizard's PracticeForm (no Where toggle there). Every entry here
// must also be a leaf of the matching `DISTANCE_TREE_*` branch below, so a
// common chip and its tree branch can never disagree (pinned by test).
export const PRACTICE_COMMON_INDOOR_M = [18, 20, 25];
export const PRACTICE_COMMON_INDOOR_YD = [15, 20, 25];
export const PRACTICE_COMMON_OUTDOOR_M = [30, 50, 70];
export const PRACTICE_COMMON_OUTDOOR_YD = [40, 60, 80];

// The chip a Where flip lands on, per branch and unit (owner spec: indoor →
// 18 m / 20 yd, outdoor → 50 m / 60 yd) — the archer's most-shot distance on
// that branch rather than a stale pick from the other one.
export const PRACTICE_WHERE_LANDING = {
  indoor: { m: 18, yd: 20 },
  outdoor: { m: 50, yd: 60 },
};

// The full Indoor/Outdoor distance tree, per unit (#2348 owner spec). Order
// matters — rendered ascending, same as the old flat chip lists it replaces.
export const DISTANCE_TREE_M = {
  Indoor: [9, 10, 15, 18, 20, 25],
  Outdoor: [30, 40, 50, 60, 70, 90, 100],
};
export const DISTANCE_TREE_YD = {
  Indoor: [10, 15, 20, 25],
  Outdoor: [30, 40, 50, 60, 80, 100],
};

// Free-entry bounds for every branch's "Other…" input — same generous 1–200
// range the old top-level Other… chip used (#2342), now offered once per
// branch inside the tree rather than once on the row.
export const OTHER_DISTANCE_MIN = 1;
export const OTHER_DISTANCE_MAX = 200;

// ── Save-format nudge (#2323) ─────────────────────────────────────────────────
// Post-round "Save this as a format?" — offered on Round Detail for a
// completed round with no `round_type_id` (a one-off Simple Round or custom
// schedule). Two pure questions live here, mirroring the rest of this file's
// "does this row match?" shape: what should the format be CALLED by default,
// and does an identical shape already exist in the catalog (in which case the
// nudge offers "use it next time" instead of "save a new one").

import faceDefinitionsForZones from "../../../shared/face-definitions.json";

const { faces: ZONE_FACES, faceTypes: ZONE_FACE_TYPES } = faceDefinitionsForZones;

/**
 * A short "<N>-<zone|ring|spot>" descriptor for a face's ring layout (#2323),
 * e.g. "5-zone" for Imperial 5-Zone, "6-ring" for WA 6-Ring, "5-spot" for NFAA
 * 5-Spot. Read from the face-type's own `display_label` in
 * shared/face-definitions.json rather than hand-listing zone counts per face,
 * so a JSON addition never needs a change here. A label with no such count
 * (e.g. plain "WA Full") falls back to "<max_arrow>-zone".
 *
 * @param {string} face  a target_face code
 * @returns {string}
 */
export function zoneDescriptor(face) {
  const faceType = ZONE_FACES[face]?.face_type;
  const entry = faceType ? ZONE_FACE_TYPES[faceType] : null;
  const label = entry?.display_label || "";
  const m = label.match(/(\d+)-(zone|ring|spot)/i);
  if (m) return `${m[1]}-${m[2].toLowerCase()}`;
  return `${entry?.max_arrow ?? 10}-zone`;
}

/**
 * The descriptive default name for a save-format nudge, e.g.
 * "5-zone · 20 yd · 10×3" (#2323). Reads the round's own FIRST segment for the
 * native distance + unit (unit-native, same reasoning as `toMetres` above — a
 * 20 yd round must never be relabelled "18 m"), falling back to `distance_m`
 * for a round with no stored segments (pre-#1319 legacy data). Ends/arrows
 * come from the round's own totals, not the segment, since those are always
 * populated even when `segments` is null.
 *
 * A genuinely unmarked round (course segment `distance: null`, `distance_m`
 * also `null` since migration 0112) omits the distance clause entirely —
 * "5-zone · 28×4" — rather than the old `?? 0` fallback, which read as a
 * fabricated "0 m" (QA 2026-09-13 Bug 2 follow-up).
 *
 * @param {object} round  a RoundSummary/RoundDetail payload
 * @returns {string}
 */
export function defaultFormatName(round) {
  const seg = round?.segments?.[0];
  const distance = seg?.distance ?? round?.distance_m ?? null;
  const unit = seg?.unit || "m";
  const ends = round?.ends_total ?? 0;
  const arrowsPerEnd = round?.arrows_per_end ?? 0;
  const parts = [zoneDescriptor(round?.target_face)];
  if (distance != null) parts.push(`${distance} ${unit}`);
  parts.push(`${ends}×${arrowsPerEnd}`);
  return parts.join(" · ");
}

/**
 * The `segments` array for a "save this round as a format" payload (#2323) —
 * same v2 shape `stagesToSegments` (customRoundStages.js) emits for the
 * wizard's own custom builder. The round's OWN `segments` (as read back from
 * the API) carry `face_type`/`face_size_cm`, not a face CODE — the shape
 * `PersonalRoundTypeCreate` needs — so every segment is stamped with the
 * round's own `target_face`. This is exact for the overwhelming common case
 * (a single-segment Simple Round or one-off custom schedule all shot on one
 * face); a multi-distance round whose segments used more than one face SIZE
 * within the same scoring family (e.g. a hand-built WA720-shaped schedule)
 * would flatten to the round's face for every segment — an acceptable
 * simplification for a nudge whose whole job is to save exactly what was
 * just shot, not to rebuild a division-aware catalog row.
 *
 * @param {object} round  a RoundSummary/RoundDetail payload
 * @returns {Array}
 */
export function segmentsForSaveAsFormat(round) {
  const face = round?.target_face || "wa40";
  const segs = Array.isArray(round?.segments) && round.segments.length > 0
    ? round.segments
    : [{
        distance: round?.distance_m ?? 18,
        unit: "m",
        ends: round?.ends_total ?? 10,
        arrows_per_end: round?.arrows_per_end ?? 3,
        label: null,
      }];
  return segs.map((s) => ({
    distance: s.distance ?? round?.distance_m ?? null,
    unit: s.unit || "m",
    ends: Number(s.ends) || 0,
    arrows_per_end: Number(s.arrows_per_end) || round?.arrows_per_end || null,
    face,
    label: s.label || null,
  }));
}

/**
 * Does a completed, unmatched round's shape (face + arrows-per-end + full
 * segment schedule) exactly match an existing CATALOG format (#2323)? When it
 * does, `SaveFormatNudge` offers "This looks like <name> — use it next time?"
 * instead of a save prompt — there is no point saving a personal duplicate of
 * a format that already exists.
 *
 * Compares the round-level `target_face`/`arrows_per_end` first (cheap, rules
 * out most rows immediately), then the normalized segment list against each
 * candidate's own `segment_template` (falling back to a single segment built
 * from `distances[0]`/`ends`/`arrows_per_end`/`unit` for an older catalog row
 * seeded before segment_template existed — same fallback shape
 * `stagesFromPreset` uses on the FORMAT step). Personal formats (someone's own
 * saved custom round) and legacy rows are never offered back as a match —
 * only live catalog rows make sense as "use it next time".
 *
 * @param {object} round  a RoundSummary/RoundDetail payload
 * @param {Array} roundTypes  the whole catalog (GET /api/round-types)
 * @returns {object|null} the matching catalog row, or null
 */
export function matchingCatalogFormat(round, roundTypes) {
  const segments = round?.segments;
  if (!Array.isArray(segments) || segments.length === 0) return null;
  const face = round?.target_face;
  const arrowsPerEnd = round?.arrows_per_end;

  const normalize = (segs, fallbackArrows) =>
    (segs || []).map((s) => ({
      distance: s.distance ?? null,
      unit: s.unit || "m",
      ends: Number(s.ends) || 0,
      arrows_per_end: Number(s.arrows_per_end) || fallbackArrows,
    }));

  const roundShape = JSON.stringify(normalize(segments, arrowsPerEnd));

  return (
    (roundTypes ?? []).find((rt) => {
      if (rt.environment === "legacy" || rt.owner_archer_id != null) return false;
      if (rt.face !== face) return false;
      if (rt.arrows_per_end !== arrowsPerEnd) return false;
      const rtSegs =
        Array.isArray(rt.segment_template) && rt.segment_template.length > 0
          ? rt.segment_template
          : [{
              distance: rt.distances?.[0] ?? null,
              unit: rt.unit || "m",
              ends: rt.ends,
              arrows_per_end: rt.arrows_per_end,
            }];
      return JSON.stringify(normalize(rtSegs, rt.arrows_per_end)) === roundShape;
    }) || null
  );
}
